import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { lazy, Suspense } from "react";
import { LinkButton } from "../components/Button";
import { Card, EmptyState, SectionHeader } from "../components/UI";
import { formatCurrency, resolveAssetUrl } from "../lib/api";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

export function CartPage() {
  const { cart, removeCartItem, updateCartQuantity } = useAppState();
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (!cart.length) {
    return (
      <section>
        <SectionHeader title="Cart" className="page-hero" />
        <EmptyState icon={<ShoppingBag size={24} />} action={<LinkButton to="/products">Browse Catalog</LinkButton>}>
          Your cart is empty.
        </EmptyState>
      </section>
    );
  }

  return (
    <section>
      <SectionHeader title="Cart" className="page-hero cart-page-heading" />
      <div className="cart-page-layout">
        <div className="cart-list-panel">
        {cart.map((item) => (
          <article className="cart-item-row" key={item.id}>
            <div className="cart-item-media">
              {item.image ? (
                <img src={resolveAssetUrl(item.image)} alt={item.name} loading="lazy" />
              ) : item.stl_file || item.stlFile ? (
                <Suspense fallback={<div className="cart-model-loading"><ShoppingBag size={20} aria-hidden="true" /></div>}>
                  <STLViewer
                    url={resolveAssetUrl(item.stl_file || item.stlFile)}
                    color={item.stlColor || String(item.quote_options?.color || "Blue")}
                    colorHex={item.stlColorHex}
                    mode="product"
                    interactive={false}
                    autoRotate={false}
                    className="stl-viewer-static"
                  />
                </Suspense>
              ) : (
                <div className="cart-model-loading">
                  <ShoppingBag size={20} aria-hidden="true" />
                </div>
              )}
            </div>
            <div className="cart-item-main">
              <strong>{item.name}</strong>
              <div className="cart-item-detail">{item.detail || (item.source === "quote" ? "Custom STL Quote" : "Standard Product")}</div>
              {item.volume_cm3 || item.weight_grams ? (
                <div className="cart-item-metrics">
                  {item.volume_cm3 ? `Vol: ${item.volume_cm3.toFixed(1)} cm3` : ""}
                  {item.volume_cm3 && item.weight_grams ? " · " : ""}
                  {item.weight_grams ? `${item.weight_grams.toFixed(1)} g` : ""}
                </div>
              ) : null}
            </div>
            <div className="cart-qty-control" role="group" aria-label={`Quantity selector for ${item.name}`} data-min="1">
              <button
                type="button"
                aria-label="Decrease Quantity"
                disabled={item.quantity <= 1}
                onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
              >
                <Minus size={16} aria-hidden="true" />
              </button>
              <output aria-live="polite">{item.quantity}</output>
              <button
                type="button"
                aria-label="Increase Quantity"
                onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
              >
                <Plus size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="cart-item-price">
              <span>{formatCurrency(item.price * item.quantity)}</span>
              {item.quantity > 1 ? <small>{formatCurrency(item.price)} each</small> : null}
            </div>
            <button
              className="cart-remove-btn"
              type="button"
              aria-label={`Remove ${item.name}`}
              onClick={() => removeCartItem(item.id)}
            >
              <Trash2 size={18} aria-hidden="true" />
            </button>
          </article>
        ))}
          <div className="cart-list-footer">
            <LinkButton to="/products" variant="ghost" className="cart-continue-link">Continue Shopping</LinkButton>
            <span id="cart-list-subtotal">Subtotal: {formatCurrency(subtotal)}</span>
          </div>
        </div>

        <Card className="cart-summary cart-summary-panel">
          <h3>Summary</h3>
          {cart.map((item) => (
            <div className="summary-row summary-item-row" key={`summary-${item.id}`}>
              <div className="summary-item-main">
                <div className="summary-item-meta">
                  <span className="summary-item-name">{item.stl_filename || item.name}</span>
                </div>
              </div>
              <span className="summary-item-price">{formatCurrency(item.price * item.quantity)}</span>
            </div>
          ))}
          <div className="summary-row"><span>Subtotal</span><span id="cart-summary-subtotal">{formatCurrency(subtotal)}</span></div>
          <div className="text-sm text-muted" id="cart-summary-shipping-note" style={{ marginTop: "var(--space-sm)", lineHeight: 1.5 }}>
            Shipping will be calculated at checkout after you choose your shipping address.
          </div>
          <div className="summary-total"><span>Total</span><span id="cart-summary-total">{formatCurrency(subtotal)}</span></div>
          <LinkButton to="/checkout" className="btn-block">Proceed to Checkout</LinkButton>
        </Card>
      </div>
    </section>
  );
}
